import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../../components/admin/AdminHeader';
import authService from '../../../services/authService';
import api from '../../../services/api';

/**
 * Redemption Management Page
 * Manage redemption catalog for partners
 */
export default function RedemptionManagementPage() {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'history'
  
  // Redemption history
  const [redemptions, setRedemptions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filters, setFilters] = useState({
    productId: '',
    startDate: '',
    endDate: '',
    userEmail: ''
  });

  // Partner selection for super admins
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    productType: 'subscription_discount',
    pointsCost: 100,
    discount: {
      type: 'percentage',
      value: 3,
      maxPercent: 75,
      maxFixed: 100,
      duration: 'recurring'
    },
    limits: {
      perUser: 1,
      totalQuantity: 0,
      maxPointsPerRedemption: 2500
    },
    externalRef: '',
    webhookUrl: '',
    redemptionUrl: '',
    imageUrl: '',
    status: 'active'
  });

  useEffect(() => {
    if (!authService.isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    
    const userData = authService.getAdminUser();
    if (!userData) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(userData);
    
    // If super admin, load partners list
    if (userData.role === 'super') {
      loadPartners();
    } else {
      // Regular admin - use their partner
      setSelectedPartnerId(userData.partnerId);
      loadProducts(userData.partnerId);
    }
  }, [navigate]);

  // Load products when selected partner changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadProducts(selectedPartnerId);
    }
  }, [selectedPartnerId]);

  // Load history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history' && selectedPartnerId) {
      loadRedemptionHistory(selectedPartnerId);
    }
  }, [activeTab, selectedPartnerId, filters]);

  const loadPartners = async () => {
    const token = authService.getAdminToken();
    if (!token) {
      console.log('[RedemptionManagement] No admin token available');
      return;
    }
    
    try {
      const response = await fetch('/api/admin/partners', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-client-token': 'cnxn_admin_token'
        }
      });
      const data = await response.json();
      setPartners(data.partners || []);
      
      // Auto-select first partner if available
      if (data.partners && data.partners.length > 0) {
        setSelectedPartnerId(data.partners[0]._id);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadProducts = async (partnerId) => {
    if (!partnerId) {
      setLoading(false);
      return;
    }

    const token = authService.getAdminToken();
    if (!token) {
      console.log('[RedemptionManagement] No admin token available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const url = `/api/admin/redemption/products/${partnerId}?status=active`;
      console.log('[RedemptionManagement] Fetching products from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-client-token': 'cnxn_admin_token'
        }
      });
      
      console.log('[RedemptionManagement] Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please log out and log back in.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        console.log('[RedemptionManagement] Error data:', errorData);
        throw new Error(errorData.error || 'Partner not found');
      }
      
      const data = await response.json();
      setProducts(data.data || []);
    } catch (err) {
      console.error('Error loading products:', err);
      if (!error) setError(err.message || 'Failed to load redemption products');
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptionHistory = async (partnerId) => {
    const token = authService.getAdminToken();
    if (!token || !partnerId) return;
    
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.productId) params.append('productId', filters.productId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userEmail) params.append('userEmail', filters.userEmail);
      
      const url = `/api/admin/redemption/history/${partnerId}?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-client-token': 'cnxn_admin_token'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load redemption history');
      }
      
      const data = await response.json();
      setRedemptions(data.data || []);
    } catch (err) {
      console.error('Error loading redemption history:', err);
      setError(err.message || 'Failed to load redemption history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUndoRedemption = async (redemptionId) => {
    if (!confirm('Are you sure you want to undo this redemption? Points will be returned to the user.')) {
      return;
    }
    
    const token = authService.getAdminToken();
    if (!token) return;
    
    try {
      const response = await fetch(`/api/admin/redemption/${selectedPartnerId}/${redemptionId}/undo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-client-token': 'cnxn_admin_token',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to undo redemption');
      }
      
      setSuccess('Redemption undone successfully. Points returned to user.');
      loadRedemptionHistory(selectedPartnerId);
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error undoing redemption:', err);
      setError(err.message || 'Failed to undo redemption');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      productType: 'subscription_discount',
      pointsCost: 100,
      discount: {
        type: 'percentage',
        value: 10,
        maxAmount: 0,
        duration: 'recurring'
      },
      limits: {
        perUser: 1,
        totalQuantity: 0
      },
      externalRef: '',
      webhookUrl: '',
      redemptionUrl: '',
      imageUrl: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      productType: product.productType,
      pointsCost: product.pointsCost,
      discount: product.discount || {
        type: 'percentage',
        value: 10,
        maxAmount: 0,
        duration: 'recurring'
      },
      limits: product.limits || {
        perUser: 1,
        totalQuantity: 0
      },
      externalRef: product.externalRef || '',
      webhookUrl: product.webhookUrl || '',
      redemptionUrl: product.redemptionUrl || '',
      imageUrl: product.imageUrl || '',
      status: product.status
    });
    setShowModal(true);
  };

  const handleSaveProduct = async () => {
    if (!selectedPartnerId) {
      setError('Please select a partner');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const headers = {
        'Authorization': `Bearer ${authService.getAdminToken()}`,
        'Content-Type': 'application/json',
        'x-client-token': 'cnxn_admin_token'
      };

      if (editingProduct) {
        const response = await fetch(`/api/admin/redemption/products/${selectedPartnerId}/${editingProduct._id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update product');
        }
        
        setSuccess('Product updated successfully');
      } else {
        const response = await fetch(`/api/admin/redemption/products/${selectedPartnerId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create product');
        }
        
        setSuccess('Product created successfully');
      }

      setShowModal(false);
      await loadProducts(selectedPartnerId);
    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.message || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to archive this product?')) return;

    if (!selectedPartnerId) {
      setError('Please select a partner');
      return;
    }

    try {
      setError(null);
      
      const response = await fetch(`/api/admin/redemption/products/${selectedPartnerId}/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to archive product');
      }
      
      setSuccess('Product archived successfully');
      await loadProducts(selectedPartnerId);
    } catch (err) {
      console.error('Error archiving product:', err);
      setError(err.message || 'Failed to archive product');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDiscountChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      discount: {
        ...prev.discount,
        [field]: value
      }
    }));
  };

  const handleLimitsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [field]: value
      }
    }));
  };

  const formatProductType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDuration = (duration) => {
    const map = {
      'once': 'One Time',
      'recurring': 'Recurring',
      'lifetime': 'Lifetime'
    };
    return map[duration] || duration;
  };

  if (!adminUser) {
    return <div style={{ padding: '24px' }}>Loading...</div>;
  }

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Partner Selection for Super Admins */}
        {adminUser.role === 'super' && (
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <label style={{ 
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              fontSize: '14px'
            }}>
              Select Partner
            </label>
            <select
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">-- Select a Partner --</option>
              {partners.map(partner => (
                <option key={partner._id} value={partner._id}>
                  {partner.displayName} ({partner.partnerKey})
                </option>
              ))}
            </select>
          </div>
        )}

        {!selectedPartnerId ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>
              Please select a partner to manage redemption products
            </p>
          </div>
        ) : loading ? (
          <div style={{ padding: '24px' }}>Loading...</div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div style={{
              display: 'flex',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => setActiveTab('catalog')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'catalog' ? '2px solid #4f46e5' : '2px solid transparent',
                  color: activeTab === 'catalog' ? '#4f46e5' : '#6b7280',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  transition: 'all 0.2s'
                }}
              >
                Product Catalog
              </button>
              <button
                onClick={() => setActiveTab('history')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'history' ? '2px solid #4f46e5' : '2px solid transparent',
                  color: activeTab === 'history' ? '#4f46e5' : '#6b7280',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  transition: 'all 0.2s'
                }}
              >
                Redemption History
              </button>
            </div>

            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0' }}>
            {activeTab === 'catalog' ? 'Redemption Catalog' : 'Redemption History'}
          </h1>
          <p style={{ margin: 0, color: '#666' }}>
            {activeTab === 'catalog' 
              ? 'Manage rewards that users can redeem with their points'
              : 'View and manage user redemptions'
            }
          </p>
        </div>
        {activeTab === 'catalog' && (
          <button
            onClick={handleCreateProduct}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            + Create Product
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#efe',
          color: '#3a3',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Catalog Tab Content */}
      {activeTab === 'catalog' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {products.map(product => (
          <div
            key={product._id}
            style={{
              border: '1px solid #e5e5e5',
              borderRadius: '12px',
              padding: '20px',
              backgroundColor: 'white',
              position: 'relative'
            }}
          >
            {/* Status Badge */}
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600,
              backgroundColor: product.status === 'active' ? '#dfd' : '#ddd',
              color: product.status === 'active' ? '#363' : '#666'
            }}>
              {product.status}
            </div>

            {/* Product Info */}
            <h3 style={{ margin: '0 0 8px 0', paddingRight: '80px' }}>
              {product.name}
            </h3>
            <p style={{ 
              margin: '0 0 16px 0', 
              color: '#666', 
              fontSize: '14px',
              minHeight: '40px'
            }}>
              {product.description}
            </p>

            {/* Details */}
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Type:</strong> {formatProductType(product.productType)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Increment:</strong> {product.pointsCost} pts
              </div>
              {product.discount && (
                <>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Rate:</strong>{' '}
                    {product.discount.type === 'percentage' 
                      ? `${product.discount.value}% per increment`
                      : `$${product.discount.value} per increment`}
                  </div>
                  {product.discount.maxPercent && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Max %:</strong> {product.discount.maxPercent}%
                    </div>
                  )}
                  {product.discount.maxFixed > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Max $:</strong> ${product.discount.maxFixed}
                    </div>
                  )}
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Duration:</strong> {formatDuration(product.discount.duration)}
                  </div>
                </>
              )}
              <div style={{ marginBottom: '8px' }}>
                <strong>Per User:</strong> {product.limits.perUser === 0 ? 'Unlimited' : product.limits.perUser}
              </div>
              {product.limits.maxPointsPerRedemption > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Max Points/Redemption:</strong> {product.limits.maxPointsPerRedemption}
                </div>
              )}
            </div>

            {product.limits.totalQuantity > 0 && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                <strong>Remaining:</strong> {product.limits.remainingQuantity} / {product.limits.totalQuantity}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleEditProduct(product)}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteProduct(product._id)}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Archive
              </button>
            </div>
          </div>
        ))}

        {products.length === 0 && (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            <p style={{ fontSize: '18px', marginBottom: '12px' }}>
              No redemption products yet
            </p>
            <p style={{ fontSize: '14px' }}>
              Create your first product to start offering rewards
            </p>
          </div>
        )}
        </div>
      )}

      {/* History Tab Content */}
      {activeTab === 'history' && (
        <>
          {/* Filters */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                Product
              </label>
              <select
                value={filters.productId}
                onChange={(e) => setFilters({...filters, productId: e.target.value})}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Products</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                User Email
              </label>
              <input
                type="text"
                value={filters.userEmail}
                onChange={(e) => setFilters({...filters, userEmail: e.target.value})}
                placeholder="user@example.com"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Redemptions Table */}
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading redemption history...
            </div>
          ) : redemptions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>No redemptions found</p>
              <p style={{ fontSize: '14px' }}>Try adjusting your filters</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Product</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Points</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Code</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Discount</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map(redemption => (
                    <tr key={redemption._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {new Date(redemption.redemptionDate).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {redemption.userEmail || 'N/A'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {redemption.productSnapshot?.name || 'Unknown'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: 600 }}>
                        {redemption.pointsUsed}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>
                        {redemption.redemptionCode || '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {redemption.calculatedDiscount?.effectiveDiscount || '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: 
                            redemption.status === 'confirmed' || redemption.status === 'applied' ? '#dfd' :
                            redemption.status === 'cancelled' ? '#fee' : '#f0f0f0',
                          color: 
                            redemption.status === 'confirmed' || redemption.status === 'applied' ? '#363' :
                            redemption.status === 'cancelled' ? '#c33' : '#666'
                        }}>
                          {redemption.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {(redemption.status === 'confirmed' || redemption.status === 'applied') && (
                          <button
                            onClick={() => handleUndoRedemption(redemption._id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fbbf24',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600
                            }}
                          >
                            Undo
                          </button>
                        )}
                        {redemption.status === 'cancelled' && (
                          <span style={{ color: '#999', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
          </>
        )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>
              {editingProduct ? 'Edit Product' : 'Create Product'}
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Product Type *
                </label>
                <select
                  value={formData.productType}
                  onChange={(e) => handleFormChange('productType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }}
                >
                  <option value="subscription_discount">Subscription Discount</option>
                  <option value="one_time_discount">One-Time Discount</option>
                  <option value="credit">Credit</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Points Cost (Minimum Increment) *
                </label>
                <input
                  type="number"
                  value={formData.pointsCost}
                  onChange={(e) => handleFormChange('pointsCost', parseInt(e.target.value) || 0)}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Users can redeem in multiples of this amount (e.g., 100, 200, 300 pts)
                </small>
              </div>
            </div>

            <fieldset style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <legend style={{ fontWeight: 600, padding: '0 8px' }}>Discount Settings</legend>
              
              <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                <small style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                  <strong>Dynamic Calculation:</strong> Discount = (Points ÷ Increment) × Rate per Increment<br/>
                  Example: 2500pts ÷ 100pts = 25 increments × 3% = 75% discount
                </small>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Type
                  </label>
                  <select
                    value={formData.discount.type}
                    onChange={(e) => handleDiscountChange('type', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Dollar Amount</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Rate per Increment
                  </label>
                  <input
                    type="number"
                    value={formData.discount.value}
                    onChange={(e) => handleDiscountChange('value', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.1"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    {formData.discount.type === 'percentage' ? '% per increment' : '$ per increment'}
                  </small>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Max Discount % (1-100)
                  </label>
                  <input
                    type="number"
                    value={formData.discount.maxPercent || ''}
                    onChange={(e) => handleDiscountChange('maxPercent', parseInt(e.target.value) || null)}
                    min="1"
                    max="100"
                    placeholder="Optional"
                    disabled={formData.discount.type === 'fixed'}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: formData.discount.type === 'fixed' ? '#f9fafb' : 'white'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Caps calculated percentage (e.g., 75%)
                  </small>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Max Discount $ (0 = no cap)
                  </label>
                  <input
                    type="number"
                    value={formData.discount.maxFixed || 0}
                    onChange={(e) => handleDiscountChange('maxFixed', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Caps dollar amount (e.g., $100)
                  </small>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Duration
                </label>
                <select
                  value={formData.discount.duration}
                  onChange={(e) => handleDiscountChange('duration', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }}
                >
                  <option value="once">One Time</option>
                  <option value="recurring">Recurring (every billing period)</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
            </fieldset>

            <fieldset style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <legend style={{ fontWeight: 600, padding: '0 8px' }}>Limits</legend>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Per User (0 = unlimited)
                  </label>
                  <input
                    type="number"
                    value={formData.limits.perUser}
                    onChange={(e) => handleLimitsChange('perUser', parseInt(e.target.value) || 0)}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Max active redemptions per user
                  </small>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Total Quantity (0 = unlimited)
                  </label>
                  <input
                    type="number"
                    value={formData.limits.totalQuantity}
                    onChange={(e) => handleLimitsChange('totalQuantity', parseInt(e.target.value) || 0)}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Total redemptions available (inventory)
                  </small>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Max Points per Redemption (0 = unlimited)
                </label>
                <input
                  type="number"
                  value={formData.limits.maxPointsPerRedemption || 0}
                  onChange={(e) => handleLimitsChange('maxPointsPerRedemption', parseInt(e.target.value) || 0)}
                  min="0"
                  step="100"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Maximum points in a single redemption (e.g., 2500 to cap at 25 increments)
                </small>
              </div>
            </fieldset>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                External Reference (optional)
              </label>
              <input
                type="text"
                value={formData.externalRef}
                onChange={(e) => handleFormChange('externalRef', e.target.value)}
                placeholder="e.g., subscription_plan_id"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px'
                }}
              />
              <small style={{ color: '#666' }}>Reference ID in your system</small>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                Redemption URL (optional)
              </label>
              <input
                type="text"
                value={formData.redemptionUrl}
                onChange={(e) => handleFormChange('redemptionUrl', e.target.value)}
                placeholder="e.g., https://example.com/payment?plan=pro&code={code}"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '13px'
                }}
              />
              <small style={{ color: '#666' }}>
                Direct link for users after redemption. Use <code>{'{code}'}</code> as placeholder for redemption code.
              </small>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#e5e5e5',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProduct}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {editingProduct ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
